import SampleTagsAPI from '../../../../Lib/Api/sample-tags';

/**
 * Detects image type from ArrayBuffer by examining byte signatures
 */
export const detectImageType = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);

  if (bytes[0] === 0xff && bytes[1] === 0xd8) {
    return 'image/jpeg';
  }
  if (
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png';
  }
  return 'image/jpeg';
};

/**
 * Extracts filename from various URL formats - simple approach
 * @param productImage - Image URL or filename
 * @returns Filename or the original string if no extraction needed
 */
export const extractFileKey = (
  productImage: string | undefined,
): string | undefined => {
  if (!productImage) return undefined;

  if (!productImage.includes('/')) {
    return productImage;
  }

  const urlPath = productImage.split('?')[0];
  const segments = urlPath.split('/');
  const filename = segments[segments.length - 1];

  return filename || productImage;
};

/**
 * Get image URL for PDF generation
 * @param productImage - The product image path/URL
 * @param productImageDataUrl - Data URL if available (preferred for PDFs)
 * @param sampleType - The type of sample tag (legacy parameter)
 * @returns The image URL to use in PDF
 */
export const getImageUrlForPDF = (
  productImage: string | undefined,
  productImageDataUrl: string | undefined,
): string | undefined => {
  if (productImageDataUrl) return productImageDataUrl;

  if (productImage && productImage.startsWith('https://')) {
    return productImage;
  }

  console.warn(
    'PDF generation: S3 file key without data URL, this may not display correctly:',
    productImage,
  );
  return undefined;
};

/**
 * Simple image upload utility following FileInputV2 patterns
 * @param file - File to upload
 * @param userId - User ID for the upload
 * @returns File key if successful, throws error if failed
 */
export const uploadImageIfNeeded = async (
  file: File | undefined,
  userId: string,
): Promise<string | undefined> => {
  if (!file) return undefined;

  try {
    const uploadResult = await SampleTagsAPI.uploadPhoto(userId, file);

    if (uploadResult.success && uploadResult.fileKey) {
      return uploadResult.fileKey;
    }

    throw new Error(uploadResult.error || 'Failed to upload image');
  } catch (error) {
    console.error('File upload error:', error);
    throw new Error(
      error instanceof Error ? error.message : 'Failed to upload image',
    );
  }
};

/**
 * Helper function to determine if a URL needs CORS proxy
 */
export const needsCorsProxy = (url: string): boolean => {
  const corsRestrictedDomains = [
    'fntools.fnovasystems.com',
    '5648851.app.netsuite.com',
    'fn-buyer-prod-images.s3.us-west-2.amazonaws.com',
    'fn-buyer-staging-images.s3.us-west-2.amazonaws.com',
    'api-po-service-prod-public-images.s3.us-west-2.amazonaws.com',
    'api-po-service-staging-public-images.s3.us-west-2.amazonaws.com',
  ];

  try {
    const urlObj = new URL(url);
    return corsRestrictedDomains.some(
      (domain) =>
        urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`),
    );
  } catch {
    return true;
  }
};

/**
 * Converts ArrayBuffer to base64 data URL
 */
export const convertArrayBufferToDataUrl = async (
  arrayBuffer: ArrayBuffer,
): Promise<string> => {
  const mimeType = detectImageType(arrayBuffer);
  const blob = new Blob([arrayBuffer], { type: mimeType });

  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      resolve('');
    };
    reader.readAsDataURL(blob);
  });
};

/**
 * Re-encodes an image data URL to PNG via canvas to normalize incompatible JPEGs (e.g., CMYK/progressive)
 */
export const reencodeToPng = (dataUrl: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context not available'));
        return;
      }
      ctx.drawImage(img, 0, 0);
      try {
        const pngDataUrl = canvas.toDataURL('image/png');
        resolve(pngDataUrl);
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('Failed to load image for re-encode'));
    img.src = dataUrl;
  });
